import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useInfiniteQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import { VaultCardGrouped } from 'pages/vault/components/VaultCardGrouped'
import { getAllVaultMedia } from 'services/endpoints/vault'
import { useUserProfile } from 'store'
import { useNewPostSharedMediaCacheStore } from 'store/newPostSharedMediaCacheStore'

import style from './_shared_media_modal.module.scss'
import SelectCategoriesModal from '../SelectCategoriesModal/SelectCategoriesModal'
import SortModal from '../Sort/SortModal'

const SharedMediaModal: FC<{
  submitFn?: (val: any) => void
  user?: any
  selected?: any
  userMedia?: any
  checkingData?: any
  setPurchaseMsgId?: any
  setConfirmBuyModalOpen?: any
  setSelectedMessageBuyPrice?: any
  scrollToMessage?: any
  defaultType?: string
  setMessageId?: (id: string | null) => void
}> = ({
  submitFn,
  user,
  userMedia,
  checkingData,
  setPurchaseMsgId,
  setConfirmBuyModalOpen,
  setSelectedMessageBuyPrice,
  scrollToMessage,
  setMessageId
}) => {
  const userData = useUserProfile()
  const modalData = useModalContext()
  const { id } = useParams<any>()
  const { t } = useTranslation()

  const [activeFilter, setActiveFilter] = useState('all_media')
  const [activeFilterParam, setActiveFilterParam] = useState('')
  const [media, setMedia] = useState<any[]>([])
  const [allSelectedMedia, setAllSelectedMedia] = useState<any[]>([])
  const [selectedPosts, setSelecetedPosts] = useState<any[]>([])
  const [allMediaLength, setAllMediaLength] = useState(0)
  const [postIds, setPostIds] = useState<any[]>([])
  const [groupByPost, setGroupByPost] = useState<boolean>(true)
  const [sortModalOpen, setSortModalOpen] = useState(false)
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [postsSort, setPostsSort] = useState('')
  const [postsAsc, setPostsAsc] = useState('asc')
  const [filterOption, setFilterOption] = useState('free&premium')
  const [selectedVariantKeys, setSelectedVariantKeys] = useState<string[]>([])
  const [sharedMediaUser] = useState<any>()

  const [activeCategory, setActiveCategory] = useState<any[]>([
    {
      name: id ? 'Loading...' : 'All',
      id: id ?? null,
      posts: 0,
      premium: 0,
      stories: 0,
      messages: 0,
      selected: true
    }
  ])

  const activeCategoryId = activeCategory?.[0]?.id ?? null
  const vaultType = 'all'
  const selectRemovedMedia = true

  const filterKey = useMemo(() => {
    if (userData?.role === 'model') return activeFilter || 'all_media'
    return activeFilterParam || ''
  }, [userData?.role, activeFilter, activeFilterParam])

  const cacheKey = useMemo(
    () =>
      JSON.stringify({
        vaultType,
        filterKey,
        postsSort,
        postsAsc,
        selectRemovedMedia,
        userId: user?.id,
        activeCategoryId,
        groupByPost,
        filterOption
      }),
    [
      filterKey,
      postsSort,
      postsAsc,
      selectRemovedMedia,
      user?.id,
      activeCategoryId,
      groupByPost,
      filterOption
    ]
  )

  const setCache = useNewPostSharedMediaCacheStore((s) => s.setCache)
  const cachedMedia = useNewPostSharedMediaCacheStore((s) => s.mediaMap[cacheKey])

  const getRowPhotos = (row: any) => row?.message?.message?.photos ?? row?.post?.photos ?? []
  const getRowVideos = (row: any) => row?.message?.message?.videos ?? row?.post?.videos ?? []
  const priceOf = (item: any) => item?.message?.message?.price ?? item?.post?.price ?? 0

  const getCardType = (item: any) => {
    if (item?.message) return 'messages'
    if (item?.story) return 'story'
    if (item?.post && Number(item.post?.price ?? 0) > 0) return 'premium'
    if (item?.post) return 'feed'
    return 'all'
  }

  const cloneRowWithOnly = (row: any, only: { photos?: any[]; videos?: any[] }) => {
    const photos = only.photos ?? []
    const videos = only.videos ?? []

    if (row?.message?.message) {
      return {
        ...row,
        __k: `msg-${row.message.message.id}-${videos[0]?.id ?? photos[0]?.id}`,
        message: {
          ...row.message,
          message: {
            ...row.message.message,
            photos,
            videos
          }
        }
      }
    }

    if (row?.post) {
      return {
        ...row,
        __k: `post-${row.post.id}-${videos[0]?.id ?? photos[0]?.id}`,
        post: {
          ...row.post,
          photos,
          videos
        }
      }
    }

    if (row?.story) {
      return {
        ...row,
        __k: `story-${row.story.id ?? Math.random()}`
      }
    }

    return row
  }

  const onSelectVariant = (variant: any) => {
    const key = String(
      variant.__k ??
        `${variant.id}-${variant.post ? 'post' : variant.message ? 'msg' : 'story'}`
    )

    const isSelected = selectedVariantKeys.includes(key)

    if (isSelected) {
      setSelectedVariantKeys((prev) => prev.filter((k) => k !== key))
      setAllSelectedMedia((prev) => prev.filter((v: any) => v.__k !== key))
      setAllMediaLength((n) => n - 1)
      return
    }

    if (allMediaLength + 1 > 40) {
      addToast('error', 'Only 40 media files can be selected at once')
      return
    }

    setSelectedVariantKeys((prev) => [...prev, key])
    setAllSelectedMedia((prev) => [...prev, variant])
    setAllMediaLength((n) => n + 1)
  }

  const isVariantSelected = (key: string) => selectedVariantKeys.includes(key)

  const getVariantIndex = (key: string) => {
    const index = selectedVariantKeys.indexOf(key)
    return index === -1 ? null : index + 1
  }

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    [
      'vaultMedia',
      vaultType,
      filterKey,
      postsSort,
      postsAsc,
      selectRemovedMedia,
      user?.id,
      activeCategoryId,
      groupByPost,
      filterOption
    ],
    async ({ pageParam = 0 }) => {
      return await getAllVaultMedia(vaultType, pageParam, {
        category: activeCategoryId,
        grouped: groupByPost,
        filter: filterKey,
        sort: postsSort,
        sortAsc: postsAsc,
        mapExistingPostId: false,
        offline: true,
        previously_shared: true
      })
    },
    {
      enabled: !!userData,
      getNextPageParam: (lastPage) => lastPage?.nextPage ?? false,
      retry: false,
      keepPreviousData: false,
      // staleTime: 1000 * 10,
      initialData: cachedMedia
        ? {
            pages: [{ data: cachedMedia, nextPage: null, existingIds: [] }],
            pageParams: [0]
          }
        : undefined
    }
  )
const showInitialLoader = isLoading && !cachedMedia
  useEffect(() => {
    if (cachedMedia) {
      setMedia(cachedMedia)
    }
  }, [cachedMedia])

  useEffect(() => {
    if (!data || isLoading) return

    const merged = data.pages.flatMap((page: any) => page?.data ?? [])

    setMedia(merged)
    setCache(cacheKey, merged)
  }, [data, isLoading, cacheKey, setCache])

  const observer = useRef<IntersectionObserver>()

  useEffect(() => {
    return () => observer.current?.disconnect()
  }, [])

  const lastPostRef = useCallback(
    (node: any) => {
      if (isFetchingNextPage) return

      observer.current?.disconnect()

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '150px' }
      )

      if (node) observer.current.observe(node)
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  const displayMedia = useMemo(() => {
    if (groupByPost) return media

    const takePhotos = activeFilter === 'all_media' || activeFilter === 'photos'
    const takeVideos = activeFilter === 'all_media' || activeFilter === 'videos'

    const out: any[] = []

    for (const row of media) {
      const photos = getRowPhotos(row)
      const videos = getRowVideos(row)

      if (takePhotos && Array.isArray(photos)) {
        for (const photo of photos) {
          out.push(cloneRowWithOnly(row, { photos: [photo], videos: [] }))
        }
      }

      if (takeVideos && Array.isArray(videos)) {
        for (const video of videos) {
          out.push(cloneRowWithOnly(row, { photos: [], videos: [video] }))
        }
      }
    }

    return out
  }, [media, groupByPost, activeFilter])

  const getItemKey = (row: any, grouped: boolean) => {
    if (grouped) {
      const baseId = row?.id ?? row?.message?.message?.id ?? row?.post?.id ?? row?.story?.id
      return `group-${baseId ?? Math.random()}`
    }

    return String(row?.__k ?? Math.random())
  }

  const getMediaCount = (val: any) => {
    if (val?.post) {
      return (val.post?.videos?.length ?? 0) + (val.post?.photos?.length ?? 0)
    }

    if (val?.message?.message) {
      return (
        (val.message.message?.videos?.length ?? 0) +
        (val.message.message?.photos?.length ?? 0)
      )
    }

    if (val?.story) return 1

    return 0
  }

  const toggleSelectedPosts = (val: any) => {
    const calcNumber = getMediaCount(val)

    if (val.id) {
      if (selectedPosts.includes(val.id)) {
        setSelecetedPosts((prev) => prev.filter((item: number) => item !== val.id))
      } else if (allMediaLength + calcNumber <= 40) {
        setSelecetedPosts((prev) => [...prev, val.id])
      }

      if (postIds.includes(val.id)) {
        setPostIds((prev) =>
          prev.filter((item: number) => item !== (groupByPost ? val.id : val.post_id))
        )
      } else if (allMediaLength + calcNumber <= 40) {
        setPostIds((prev) => [...prev, groupByPost ? val.id : val.post_id])
      }
    }

    if (allSelectedMedia.some((item: any) => item.id === val.id)) {
      setAllSelectedMedia((prev) => prev.filter((item: any) => item.id !== val.id))
      setAllMediaLength((prev) => prev - calcNumber)
      return
    }

    if (allMediaLength + calcNumber > 40) {
      addToast('error', 'Only 40 media files can be selected at once')
      return
    }

    setAllSelectedMedia((prev) => [...prev, val])
    setAllMediaLength((prev) => prev + calcNumber)
  }

  const feedFilter = {
    first_section: [
      { value: 'group_by_post', name: 'Group By Post', isToggle: true },
      { value: 'show_only_unreleased', name: 'Unreleased', isToggle: true }
    ],
/*     second_section: [
      { value: 'free', name: 'Free' },
      { value: 'premium', name: 'Premium' },
      { value: 'free&premium', name: 'Free & Premium' }
    ], */
    fourth_section: [
      { value: 'free', name: 'Free' },
      { value: 'premium', name: 'Premium' },
      { value: 'free&premium', name: 'Free & Premium' }
    ],
    third_section: [
      { value: 'all_media', name: 'All Media' },
      { value: 'photos', name: 'Photos' },
      { value: 'videos', name: 'Videos' }
    ]
  }

  const feedSort = {
    first_section: [
      { value: 'most_recent', name: 'Latest Uploaded' },
      { value: 'most_viewed_current_subs', name: 'Most Viewed - Current Subs' },
      { value: 'most_viewed', name: 'Most Viewed - All Time' },
      { value: 'most_purchased', name: 'Most Purchased' },
      // { value: 'most_liked', name: 'Most Liked' },
      { value: 'highest_tips', name: 'Highest Tips' },
      // { value: 'most_comment', name: 'Most Commented' }
    ],
    second_section: [
      { value: 'asc', name: 'Ascending' },
      { value: 'desc', name: 'Descending' }
    ]
  }

  const submitMedia = (): void => {
    if (!submitFn) return

    if (setMessageId) {
      setMessageId(
        allSelectedMedia.find((item: any) => item?.message?.previuos_message_ids)
          ?.message?.previuos_message_ids ?? null
      )
    }

    submitFn(allSelectedMedia)
    modalData.clearModal()
  }

  const visibleMedia = useMemo(() => {
    return displayMedia
      .filter((item: any) => (item?.story ? item.story.video_src || item.story.image_src : item))
      .filter((item: any) => {
        if (filterOption === 'premium') return Number(priceOf(item)) > 0
        if (filterOption === 'free') return Number(priceOf(item)) === 0
        return true
      })
      .filter((item: any) => {
        if (sharedMediaUser === true && item?.message?.message_send_users?.length <= 1) {
          return userMedia?.role === 'model'
            ? item?.message?.users?.[0]?.id !== userMedia?.id
            : item?.message?.users?.[1]?.id !== userMedia?.id
        }

        return true
      })
      .filter((item: any) => {
        if (sharedMediaUser !== false && item?.vaultable?.mass_message_id !== null) {
          const hasSeenUsers = item?.message?.message_seen_users?.filter(
            (x: { user_id: any; is_seen: number }) => x?.is_seen === 1
          )

          if (hasSeenUsers?.some((u: any) => u?.user_id === userMedia?.id)) return false
        }

        return true
      })
      .filter((item: any) => !(item.message === null && item.post === null && item.story === null))
      .filter((item: any) =>
        item?.message !== null ? item?.message?.message?.deleted_at === null : true
      )
  }, [displayMedia, filterOption, sharedMediaUser, userMedia])

  return (
    <div className={style.container}>
      <div className={style.title}>{t('Shared Media')}</div>

      <div className={style.title_container}>
        {userData?.role === 'model' && !user && (
          <div className={style.withHeaderSectionView}>
            <LayoutHeader
              type="title-with-buttons"
              buttons={[
                {
                  type: 'sort',
                  customClickFn: () => setSortModalOpen(true)
                },
                {
                  type: 'filter',
                  customClickFn: () => setFilterModalOpen(true)
                }
              ]}
              titleWithIcon={
                <div
                  className={style.select_category_container}
                  onClick={() => setCategoriesModalOpen(true)}
                >
                  <div className={style.select_category_icon}>
                    <spriteIcons.IconList color="#ffffff" width="14" height="14" />
                  </div>
                  <div className={style.select_category_text}>
                    {activeCategory[0]?.name} Category
                  </div>
                </div>
              }
            />

            <ModalWrapper open={categoriesModalOpen} setOpen={setCategoriesModalOpen}>
              <SelectCategoriesModal
                title="Select A Category"
                onlyOne={true}
                selectedCategories={activeCategory}
                hidePublicCategories={true}
                hideVisibilityBadges={true}
                onSave={(val: any) => {
                  setActiveFilter('all_media')

                  if (val.length > 0 && val[0].id) {
                    setActiveCategory(val)
                  } else {
                    setActiveCategory([
                      {
                        name: 'All',
                        id: null,
                        posts: 0,
                        premium: 0,
                        stories: 0,
                        messages: 0,
                        selected: true
                      }
                    ])
                  }

                  setCategoriesModalOpen(false)
                }}
                onCancel={() => setCategoriesModalOpen(false)}
                dontClearModal={true}
              />
            </ModalWrapper>

            <div className={style.sort_buttons_container}>
              <ModalWrapper open={sortModalOpen} setOpen={setSortModalOpen}>
                <div className={style.sort_modal_container}>
                  <div className={style.title}>{t('Sort')}</div>
                  <SortModal
                    applyFn={(val1: string, val2: string) => {
                      if (val1.trim() === '' || val2.trim() === '') {
                        setPostsSort('')
                        setPostsAsc('')
                        setSortModalOpen(false)
                        return
                      }

                      setPostsSort(val1)
                      setPostsAsc(val2)
                      setSortModalOpen(false)
                    }}
                    sortingProps={{
                      selectedSort: postsSort,
                      selectedOrder: postsAsc
                    }}
                    dontClearModal={true}
                    elements={feedSort}
                  />
                </div>
              </ModalWrapper>

              <ModalWrapper open={filterModalOpen} setOpen={setFilterModalOpen}>
                <div className={style.sort_modal_container}>
                  <div className={style.title}>{t('Filter')}</div>
                  <SortModal
                    applyFn={(_val1, val2, _selectMap, toggles) => {
                      setGroupByPost(!!toggles?.group_by_post)
                      if (typeof val2 === 'string') setActiveFilter(val2)
                      setFilterModalOpen(false)
                    }}
                    sortingProps={{
                      initialToggles: { group_by_post: groupByPost },
                      selectedOrder: activeFilter,
                      filterOption
                    }}
                    elements={feedFilter}
                    dontClearModal
                  />
                </div>
              </ModalWrapper>
            </div>
          </div>
        )}
      </div>

      <div className={style.cards_container}>
        {showInitialLoader ? (
          <div className={style.media_loader}>
            <BeatLoader size={8} />
          </div>
        ) : (
          visibleMedia.map((m: any) => (
            <VaultCardGrouped
              key={groupByPost ? getItemKey(m, true) : String(m.__k)}
              onCirlceClick={toggleSelectedPosts}
              onSelectVariant={onSelectVariant}
              isVariantSelected={isVariantSelected}
              getVariantIndex={getVariantIndex}
              media={m}
              selected={groupByPost ? selectedPosts.includes(m.id) : isVariantSelected(String(m.__k))}
              selectedText={
                groupByPost
                  ? selectedPosts.indexOf(m.id) + 1
                  : getVariantIndex(String(m.__k)) ?? undefined
              }
              grouped={groupByPost}
              customClass={style.mobileView}
              customCirlce={style.customCirlce}
              userMedia={userMedia}
              checkingData={checkingData}
              type={getCardType(m)}
              setPurchaseMsgId={setPurchaseMsgId}
              setConfirmBuyModalOpen={setConfirmBuyModalOpen}
              setSelectedMessageBuyPrice={setSelectedMessageBuyPrice}
              scrollToMessage={scrollToMessage}
            />
          ))
        )}

        {(hasNextPage ?? isLoading) && (
          <div style={{ width: '100%' }} ref={lastPostRef}>
            <div className="loader"></div>
          </div>
        )}
      </div>

      <div className={style.buttons_container}>
        <Button color="grey" text={t('close')} clickFn={() => modalData.clearModal()} />

        {userData?.role !== 'fan' && (
          <Button color="black" text={t(user ? 'resend' : 'addMedia')} clickFn={submitMedia} />
        )}
      </div>
    </div>
  )
}

export default SharedMediaModal